import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormLayout } from '@/components/layout';
import { Checkbox } from '@/components/ui/inputs/Checkbox';
import { LabelInput } from '@/components/ui/inputs/LabelInput';
import { Selector } from '@/components/ui/inputs/selector/Selector';
import type { SelectorOption } from '@/components/ui/inputs/selector/Selector.types';
import type { AttendanceConfigForm } from '@/schemas/attendance/attendanceConfiguration.schema';


interface AttendanceConfigurationGeneralFormProps {
  form: AttendanceConfigForm;
  markingEventOptions: SelectorOption[];
  eventDefinitionMethodOptions: SelectorOption[];
}

const handleSubmit = (event: React.FormEvent) => {
  event.preventDefault();
};

export const AttendanceConfigurationGeneralForm: React.FC<
  AttendanceConfigurationGeneralFormProps
> = ({
  form,
  markingEventOptions,
  eventDefinitionMethodOptions,
}) => {
  const { t: translate } = useTranslation('attendance');
  const fields = translate('configuration.fields', { returnObjects: true }) as Record<string, string>;
  const sections = translate('configuration.sections', { returnObjects: true }) as Record<string, string>;
  const tooltips = translate('configuration.tooltips', { returnObjects: true }) as Record<string, string>;
  const { watch, setValue, errors } = form;

  const translateError = (message: string | undefined) => message ? translate(message) : undefined;

  return (
    <FormLayout onSubmit={handleSubmit} columns={4}>
      {/* Sección Marcajes */}
      <FormLayout.Section title={sections.markings}>
        <Checkbox
          label={fields.geoOfflineRequired}
          checked={watch('geoOfflineRequired')}
          onChange={(checked) => setValue('geoOfflineRequired', checked, { shouldDirty: true })}
        />
        <Checkbox
          label={fields.collationMarkings}
          checked={watch('collationMarkings')}
          onChange={(checked) => setValue('collationMarkings', checked, { shouldDirty: true })}
        />
        <Checkbox
          label={fields.geoWebMarking}
          checked={watch('geoWebMarking')}
          onChange={(checked) => setValue('geoWebMarking', checked, { shouldDirty: true })}
        />
        <Checkbox
          label={fields.disableGeoMarking}
          tooltipText={tooltips.disableGeoMarking}
          checked={watch('disableGeoMarking')}
          onChange={(checked) => setValue('disableGeoMarking', checked, { shouldDirty: true })}
        />
        <Checkbox
          label={fields.geoInconsistencyAlert}
          checked={watch('geoInconsistencyAlert')}
          onChange={(checked) => setValue('geoInconsistencyAlert', checked, { shouldDirty: true })}
        />
        <Checkbox
          label={fields.employeeMarkingApproval}
          tooltipText={tooltips.employeeMarkingApproval}
          checked={watch('employeeMarkingApproval')}
          onChange={(checked) => setValue('employeeMarkingApproval', checked, { shouldDirty: true })}
        />
        <LabelInput
          label={fields.approvalDeadlineHours}
          type="number"
          value={String(watch('approvalDeadlineHours') ?? '')}
          onChange={(value) => setValue('approvalDeadlineHours', value === '' ? null : Number(value), { shouldDirty: true })}
          disabled={!watch('employeeMarkingApproval')}
          color={errors.approvalDeadlineHours ? 'error' : 'default'}
          supportingText={translateError(errors.approvalDeadlineHours?.message)}
        />
        <Checkbox
          label={fields.todayMarkingModification}
          tooltipText={tooltips.todayMarkingModification}
          checked={watch('todayMarkingModification')}
          onChange={(checked) => setValue('todayMarkingModification', checked, { shouldDirty: true })}
        />
      </FormLayout.Section>

      {/* Sección Eventos */}
      <FormLayout.Section title={sections.events}>
        <Selector
          label={fields.eventDefinitionMethod}
          options={eventDefinitionMethodOptions}
          value={watch('eventDefinitionMethod') || undefined}
          onChange={(value) => setValue('eventDefinitionMethod', value as string, { shouldDirty: true })}
        />
        <Selector
          label={fields.entryEvent}
          options={markingEventOptions}
          value={watch('entryEvent') !== null ? String(watch('entryEvent')) : undefined}
          onChange={(value) => setValue('entryEvent', value ? Number(value) : null, { shouldDirty: true })}
        />
        <Selector
          label={fields.exitEvent}
          options={markingEventOptions}
          value={watch('exitEvent') !== null ? String(watch('exitEvent')) : undefined}
          onChange={(value) => setValue('exitEvent', value ? Number(value) : null, { shouldDirty: true })}
        />
        <div aria-hidden="true" className="hidden md:block" />
        <LabelInput
          label={fields.collationTolerance}
          type="number"
          value={String(watch('collationTolerance') ?? '')}
          onChange={(value) => setValue('collationTolerance', value === '' ? null : Number(value), { shouldDirty: true })}
          color={errors.collationTolerance ? 'error' : 'default'}
          supportingText={translateError(errors.collationTolerance?.message)}
        />
        <Selector
          label={fields.collationEntryEvent}
          options={markingEventOptions}
          value={watch('collationEntryEvent') !== null ? String(watch('collationEntryEvent')) : undefined}
          onChange={(value) => setValue('collationEntryEvent', value ? Number(value) : null, { shouldDirty: true })}
        />
        <Selector
          label={fields.collationExitEvent}
          options={markingEventOptions}
          value={watch('collationExitEvent') !== null ? String(watch('collationExitEvent')) : undefined}
          onChange={(value) => setValue('collationExitEvent', value ? Number(value) : null, { shouldDirty: true })}
        />
      </FormLayout.Section>
    </FormLayout>
  );
};
